import {
  DiagnosticsInitResponse,
  DiagnosticsParamTypeToFieldTypeMap,
  ErrorCodeMap,
  ErrorCodes,
  ErrorType,
  EventType,
  FormConstraintV2,
  FormDataTypes,
  FormFieldOptionV2,
  FormFieldType,
  FormFieldV2,
  FormV2,
  JsonSchemaConstants,
  ParameterValueProvider,
  SafConstants,
  SAFContextVariable
} from '../models/safDiagnostic.models';
import {
  createImmutableDate,
  createImmutableList,
  createImmutableMap,
  createImmutableWeakTypedMap,
  IImmutableList,
  IImmutableMap,
  IImmutableWeakTypedMap
} from '../state/redux';

const _isNAN = (data: number): boolean => {
  return isNaN(data) || data === Infinity || data === -Infinity;
};

// tslint:disable no-any
const _requiredCheckFailed = (data: any): boolean => {
  if (data === undefined) {
    return true;
  }
  switch (typeof data) {
    case FormDataTypes.String:
      return data === null || data.length <= 0;
    case FormDataTypes.Number:
      return _isNAN(data);
    case FormDataTypes.Boolean:
      return !!data === false;
    case FormDataTypes.Object:
      return data === null;
  }
  return false;
};

const _shouldValidatePatternForNonRequiredFields = (data: any, isRequired: boolean, eventType?: string): boolean => {
  if ((!eventType || eventType === EventType.Blur) && !isRequired && _requiredCheckFailed(data)) {
    return false;
  }
  return true;
};

const _isValidateBoundsFailed = (
  data: any,
  constraints: IImmutableMap<FormConstraintV2>,
  eventType?: string
): boolean => {
  let min;
  let max;
  const isRequired = constraints.get('Required');
  if (_shouldValidatePatternForNonRequiredFields(data, isRequired, eventType)) {
    switch (typeof data) {
      case 'number':
        min = constraints.get('LowerBound');
        max = constraints.get('UpperBound');
        if (min && data < min) {
          return true;
        }
        if (max && data > max) {
          return true;
        }
        break;
      case 'string':
        min = constraints.get('MinLength');
        max = constraints.get('MaxLength');
        if (min && data.length < min) {
          return true;
        }
        if (max && data.length > max) {
          return true;
        }
        break;
    }
  }
  return false;
};

const _validatePatternFailed = (
  data: any,
  constraints: IImmutableMap<FormConstraintV2>,
  eventType?: string
): boolean => {
  let regexp;
  const pattern = constraints.get('Pattern');
  const isRequired = constraints.get('Required');
  if (_shouldValidatePatternForNonRequiredFields(data, isRequired, eventType)) {
    switch (typeof data) {
      case 'string':
        if (pattern instanceof RegExp) {
          regexp = pattern;
        } else {
          let body,
            flags = '';
          // Check for regular expression literals
          // @see http://www.ecma-international.org/ecma-262/5.1/#sec-7.8.5
          const literal = pattern.match(/^\/(.+)\/([img]*)$/);
          if (literal) {
            body = literal[1];
            flags = literal[2];
          } else {
            body = pattern;
          }
          regexp = new RegExp(body, flags);
        }
        if (!regexp.test(data)) {
          return true;
        }
    }
  }
  return false;
};

export const validateFormComponent = (
  data: any,
  constraints: IImmutableMap<FormConstraintV2>,
  eventType?: string
): ErrorType => {
  let isError = false;
  let errorType = ErrorType.None;
  if (constraints.get('Required') && _requiredCheckFailed(data)) {
    isError = true;
    errorType = ErrorType.Required;
  }
  if (
    !isError &&
    (constraints.get('LowerBound') ||
      constraints.get('UpperBound') ||
      constraints.get('MinLength') ||
      constraints.get('MaxLength')) &&
    _isValidateBoundsFailed(data, constraints, eventType)
  ) {
    isError = true;
    if (typeof data === 'number') {
      errorType = ErrorType.NumBoundary;
    } else {
      errorType = ErrorType.StringLength;
    }
  }
  if (!isError && constraints.get('Pattern') && _validatePatternFailed(data, constraints, eventType)) {
    isError = true;
    errorType = ErrorType.StringPattern;
  }
  return errorType;
};

export function validatorWrapper(
  data: any,
  componentModel: IImmutableMap<FormFieldV2>,
  onChange: (componentKey: string, updatedModel: string | number | boolean | Date | null | undefined) => void,
  onInvalid: (componentKey: string, errorCode: ErrorCodes) => void,
  onValid: (componentKey: string) => void,
  eventType: string
): void {
  const componentKey = componentModel.get('Key');
  const type = componentModel.get('Type');
  const constraints = componentModel.get('Constraints');
  const updatedModel = componentModel.toObject();
  let errorType: ErrorType;
  let errorcode = ErrorCodes.None;
  switch (type) {
    case FormFieldType.TextField:
    case FormFieldType.DropDown:
    case FormFieldType.CheckBox:
      errorType = validateFormComponent(data, constraints, eventType);
      if (errorType !== ErrorType.None) {
        updatedModel.IsError = true;
      }
      switch (errorType) {
        case ErrorType.Required:
          errorcode = constraints.get('ErrorMap').get('Required');
          break;
        case ErrorType.StringLength:
          errorcode = constraints.get('ErrorMap').get('StringLength');
          break;
        case ErrorType.StringPattern:
          errorcode = constraints.get('ErrorMap').get('Pattern');
          break;
      }
      break;
  }
  if (data !== null && data !== undefined && onChange) {
    onChange(componentKey, data);
  }
  if (onInvalid && onValid) {
    switch (errorcode) {
      case ErrorCodes.None:
        onValid(componentKey);
        break;
      default:
        onInvalid(componentKey, errorcode);
    }
  }
}

export const isFormInvalid = (form: IImmutableMap<FormV2>): boolean => {
  const formComponents = form.get('FormFields');
  let errorType = ErrorType.None;
  formComponents.forEach((component: IImmutableMap<FormFieldV2>) => {
   errorType = isFormFieldInvalid(component, errorType);
  });
  return errorType !== ErrorType.None;
};

export const isFormFieldInvalid = (formComponent: IImmutableMap<FormFieldV2>, errorType:ErrorType ): ErrorType => {
  if(formComponent){
    const data = formComponent.get('Value');
    const constraints = formComponent.get('Constraints');
    if (errorType === ErrorType.None) {
      errorType = validateFormComponent(data, constraints);
    }
  }
  return errorType;
};

export const addDays = (numDays: number, date: Date): Date => {
  const result = new Date(date.getTime());
  result.setDate(result.getDate() + numDays);
  return result;
};

// Have to be able to accept any function type
// tslint:disable-next-line: no-any
export type StyleType<T extends (...args: any[]) => any> = { [P in keyof ReturnType<T>]: any };

// Have to be able to accept any component type
// tslint:disable-next-line: no-any
export type PropsType<T extends React.ComponentClass<any>> = T extends React.ComponentClass<infer R> ? R : any;

export const getFormTitleAndDescription = (schema: IImmutableWeakTypedMap): { [key: string]: string } => {
  let localizedTitle = String();
  let localizedDescription = String();
  if (!!schema && !!schema.has(JsonSchemaConstants.Title)) {
    localizedTitle = schema.get(JsonSchemaConstants.Title);
  }
  if (!!schema && !!schema.has(JsonSchemaConstants.Description)) {
    localizedDescription = schema.get(JsonSchemaConstants.Description);
  }
  return {
    localizedTitle,
    localizedDescription
  };
};

export const getButtonLabel = (schema: IImmutableWeakTypedMap): string => {
  let btnLabel;
  if (!!schema && schema.has(JsonSchemaConstants.Action)) {
    btnLabel = schema.get(JsonSchemaConstants.Action);
  }
  return !!btnLabel && typeof btnLabel === FormDataTypes.String ? btnLabel : String();
};

export const populateRequiredProperties = (schema: IImmutableWeakTypedMap): IImmutableList<string> => {
  if (!!schema && !!schema.has(JsonSchemaConstants.Required)) {
    const required = schema.get(JsonSchemaConstants.Required);
    if (required instanceof Array) {
      return createImmutableList(required);
    }
  }
  return createImmutableList([]);
};

// tslint:disable-next-line: no-any
export const inferFieldType = (property: { [key: string]: any }) => {
  if (!!property) {
    const type = property[JsonSchemaConstants.Type];
    const format = property[JsonSchemaConstants.Format];
    const anyOf = property[JsonSchemaConstants.AnyOf];
    if (!!anyOf && anyOf instanceof Array && anyOf.length > 0) {
      return DiagnosticsParamTypeToFieldTypeMap.DropDown;
    }
    if (type === FormDataTypes.String) {
      switch (format) {
        case 'uri':
          return DiagnosticsParamTypeToFieldTypeMap.Uri;
        case 'email':
          return DiagnosticsParamTypeToFieldTypeMap.EmailAddress;
        case 'date':
          return DiagnosticsParamTypeToFieldTypeMap.DateTime;
        default:
          return DiagnosticsParamTypeToFieldTypeMap.Text;
      }
    } else if (type === FormDataTypes.Boolean) {
      return DiagnosticsParamTypeToFieldTypeMap.CheckMark;
    }
  }
  return DiagnosticsParamTypeToFieldTypeMap.None;
};

export const createFormField = (
  // tslint:disable-next-line: no-any
  property: { [key: string]: any },
  componentKey: string,
  requiredProperties: IImmutableList<string>
): IImmutableMap<FormFieldV2> | null => {
  // we don't create fields for hidden, context, global variable values, instead we prepopulate/postpopulate them inside JsonFormValues
  if (
    !!property &&
    !property[JsonSchemaConstants.Hidden] &&
    (property[JsonSchemaConstants.ValueProvider] === undefined ||
      property[JsonSchemaConstants.ValueProvider].toLowerCase() === ParameterValueProvider.userInput.toLowerCase())
  ) {
    const f = new FormFieldV2();
    f.Key = componentKey;
    f.LocalizedPlaceholder = property[JsonSchemaConstants.PlaceHolder] || String();
    f.Title = property[JsonSchemaConstants.Title];
    f.LocalizedToolTip = property[JsonSchemaConstants.ToolTip] || f.LocalizedPlaceholder || f.Title || String();
    f.Value = property[JsonSchemaConstants.Value];
    f.Type = inferFieldType(property);
    const multiLineCount = property[JsonSchemaConstants.MultiLineCount];
    f.IsMultilineText = !!multiLineCount;
    f.MultilineRowsCount = !!multiLineCount
      ? multiLineCount <= SafConstants.DiagnosticsMultilineTextFieldRowCount
        ? multiLineCount
        : SafConstants.DiagnosticsMultilineTextFieldRowCount
      : 1;

    // copy option if present
    const options = property[JsonSchemaConstants.AnyOf];
    if (!!options && options instanceof Array && options.length > 0) {
      const anyOf: IImmutableMap<FormFieldOptionV2>[] = [];
      // tslint:disable-next-line: no-any
      options!.forEach((option: { [key: string]: any }) => {
        const fOption = new FormFieldOptionV2();
        const enumArray = option[JsonSchemaConstants.Enum];
        if (!!enumArray && enumArray instanceof Array && enumArray.length > 0) {
          fOption.Key = enumArray[0] + option[JsonSchemaConstants.Title];
          fOption.Value = enumArray[0];
          fOption.LocalizedTitle = option[JsonSchemaConstants.Title];
          anyOf.push(createImmutableMap(fOption));
        }
      });
      f.AnyOf = createImmutableList(anyOf);
    }

    const validationSpecs = new FormConstraintV2();
    validationSpecs.Required = requiredProperties.includes(componentKey);
    validationSpecs.MaxLength = property[JsonSchemaConstants.MaxLength];
    validationSpecs.MinLength = property[JsonSchemaConstants.MinLength];
    validationSpecs.Pattern = property[JsonSchemaConstants.Pattern];

    const errorCodeMap = new ErrorCodeMap();
    errorCodeMap.Required = ErrorCodes.DiagnosticsFormRequiredFieldError;
    errorCodeMap.Pattern = ErrorCodes.DiagnosticsFormStringPatternError;
    errorCodeMap.StringLength = ErrorCodes.DiagnosticsFormStringLengthError;
    validationSpecs.ErrorMap = createImmutableMap(errorCodeMap);

    if (f.Type === FormFieldType.DatePicker) {
      const today = new Date(Date.now());
      validationSpecs.MinDate = createImmutableDate(addDays(SafConstants.DiagnosticsDatePickerMinDateThreshold, today));
      validationSpecs.MaxDate = createImmutableDate(today);
    }

    if (f.Type === FormFieldType.CheckBox) {
      if (property[JsonSchemaConstants.Value] === 'false' || property[JsonSchemaConstants.Default] === 'false') {
        f.Value = false;
      }

      if (property[JsonSchemaConstants.Value] === 'true' || property[JsonSchemaConstants.Default] === 'true') {
        f.Value = true;
      }
      f.Value =
        property[JsonSchemaConstants.Value] !== undefined
          ? !!property[JsonSchemaConstants.Value]
          : !!property[JsonSchemaConstants.Default];
    }

    f.Constraints = createImmutableMap(validationSpecs);
    return createImmutableMap(f);
  }
  return null;
};

export const generateFormFields = (schema: IImmutableWeakTypedMap): IImmutableMap<FormFieldV2>[] => {
  const fields: IImmutableMap<FormFieldV2>[] = [] as IImmutableMap<FormFieldV2>[];
  if (!!schema && !!schema.has(JsonSchemaConstants.Properties)) {
    const properties = schema.get(JsonSchemaConstants.Properties);
    const requiredProperties = populateRequiredProperties(schema);
    if (!!properties && typeof properties === FormDataTypes.Object) {
      const propertiesList = Object.keys(properties);
      propertiesList.forEach((componentKey: string): void => {
        if (properties.hasOwnProperty(componentKey)) {
          const field: IImmutableMap<FormFieldV2> | null = createFormField(
            properties[componentKey],
            componentKey,
            requiredProperties
          );
          if (!!field) {
            fields.push(field);
          }
        }
      });
    }
  }
  return fields;
};

export const prepopulateJsonFormValue = (schema: IImmutableWeakTypedMap): IImmutableWeakTypedMap => {
  const properties = schema.get(JsonSchemaConstants.Properties);
  let prePopulatedFormValues = createImmutableWeakTypedMap({});
  if (!!properties && typeof properties === FormDataTypes.Object) {
    const propertiesList = Object.keys(properties);
    propertiesList.forEach((propertyName: string): void => {
      if (properties.hasOwnProperty(propertyName)) {
        const property = properties[propertyName];
        // set default value to hidden property name
        if (!!property && !!property[JsonSchemaConstants.Hidden]) {
          prePopulatedFormValues = prePopulatedFormValues.set(propertyName, property[JsonSchemaConstants.Default]);
        }
      }
    });
  }
  return prePopulatedFormValues;
};

export const prepopulateVariables = (schema: IImmutableWeakTypedMap) => {
  const properties = schema.get(JsonSchemaConstants.Properties);
  const contextVariables: IImmutableMap<SAFContextVariable>[] = [];
  const globalVariables: string[] = [];
  if (!!properties && typeof properties === FormDataTypes.Object) {
    const propertiesList = Object.keys(properties);
    propertiesList.forEach((propertyName: string): void => {
      if (properties.hasOwnProperty(propertyName)) {
        const property = properties[propertyName];
        if (!!property) {
          const valueProvider = property[JsonSchemaConstants.ValueProvider];
          if (!!valueProvider) {
            if (valueProvider.toLowerCase() === ParameterValueProvider.globalVariables) {
              globalVariables.push(propertyName);
            }
            if (valueProvider.toLowerCase() === ParameterValueProvider.context) {
              let safContextParameter = createImmutableMap(new SAFContextVariable());
              safContextParameter = safContextParameter.set('Name', propertyName).set('ValueProvider', valueProvider);
              contextVariables.push(safContextParameter);
            }
          }
        }
      }
    });
  }
  return {
    contextVariables: createImmutableList(contextVariables),
    globalVariables: createImmutableList(globalVariables)
  };
};

export const populateDiagnosticsForm = (
  diagnostics: IImmutableMap<DiagnosticsInitResponse>,
  mutableForm: IImmutableMap<FormV2>,
  safAction?: boolean,
  jsonSchemaForm?: IImmutableWeakTypedMap
): IImmutableMap<FormV2> => {
  if (!!diagnostics || !!jsonSchemaForm) {
    jsonSchemaForm = jsonSchemaForm || diagnostics.get('JsonSchemaForm');
    const buttonLabel = getButtonLabel(jsonSchemaForm);
    const titleAndDescription = getFormTitleAndDescription(jsonSchemaForm);
    const fields = generateFormFields(jsonSchemaForm);
    const jsonFormValues = prepopulateJsonFormValue(jsonSchemaForm);
    const contextAndGlobalVariables = prepopulateVariables(jsonSchemaForm);
    mutableForm = mutableForm
      .set('Key', 'diagnostics_form')
      .set('LocalizedTitle', titleAndDescription.localizedTitle)
      .set('LocalizedDescription', titleAndDescription.localizedDescription)
      .set('FormFields', createImmutableList(fields))
      .set('JsonFormValues', jsonFormValues)
      .set('ButtonLabel', buttonLabel)
      .set('ContextVariables', contextAndGlobalVariables.contextVariables)
      .set('GlobalVariables', contextAndGlobalVariables.globalVariables)
      .set('ActionProvider', jsonSchemaForm.get('actionProvider'))
    if (!safAction) {
      mutableForm = mutableForm
        .set('CorrelationId', diagnostics.get('CorrelationId'))
        .set('TenantId', diagnostics.get('TenantId'))
        .set('Identifier', diagnostics.get('Identifier'))
        .set('Locale', diagnostics.get('Locale'))
    }
  }
  return mutableForm;
};
